import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, buttonClass, Card, ConfirmDialog, EmptyState, fieldClass, labelClass, LoadingList, PageHeader, SectionHeader } from '@timely/ui';
import { holds } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { Field, FormNotice } from './AuthLayout';
import { assignableRoles, whyNotEditable, type AssignableRole } from '../team/assignable';
import { paths } from '../workspace/navigation';
import {
    apiMessage, changeMemberRole, inviteMember, removeMember, resendInvite,
    revokeInvite, setMemberStatus, useTeam,
    type PendingInvite, type TeamMember,
} from '../team/useTeam';
import { isEmailShaped, normaliseEmail } from '../auth/authErrors';

/**
 * The Team screen: who is in this company, who has been asked, and the controls
 * for changing both.
 *
 * Two rules run through the whole of it.
 *
 * The first is that a control which the database will refuse is disabled here
 * **with the reason showing**, not hidden and not left live. Somebody who
 * cannot promote a colleague should see why, once, rather than press a button
 * and read a constraint violation. The refusal is still the database's — this
 * only means it is not usually reached.
 *
 * The second is that no invitation token appears anywhere in this file or in
 * anything it reads. `pending_invites` returns the address, the role and the
 * dates, and the table it reads from has no client grant at all — the token's
 * digest is not reachable even by the owner of the company. The only place a
 * raw token exists is the email, once.
 */
export function Team() {
    const context = useActiveWorkspace();
    const { members, invites, roles, loading, error, reload } = useTeam(context);

    const canManage = holds(context, 'team.manage');
    const canSeeReconciliation = canManage || holds(context, 'customers.assign');
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [confirming, setConfirming] = useState<TeamMember | null>(null);

    const offerable = assignableRoles(context, roles);

    const run = async (key: string, work: () => Promise<void>, success?: string) => {
        setBusy(key);
        setNotice(null);
        try {
            await work();
            await reload();
            if (success) setNotice({ tone: 'ok', text: success });
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof Error ? failed.message : apiMessage(failed) });
        } finally {
            setBusy(null);
        }
    };

    return (
        <Page>
            <PageHeader
                title="Team"
                subtitle="Everybody who can sign in to this company, and what they may do."
                actions={canSeeReconciliation
                    ? (
                        <Link to={paths(context.workspace.slug).setting('ownership')} className={buttonClass({ variant: 'secondary' })}>
                            Account owners
                        </Link>
                    )
                    : undefined}
            />

            {notice && (
                <div >
                    <FormNotice tone={notice.tone}>{notice.text}</FormNotice>
                </div>
            )}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading the team" rows={3} />
                ) : error ? (
                    <div className="px-5 py-6">
                        <FormNotice tone="error">{error}</FormNotice>
                        <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                    </div>
                ) : members.length === 0 ? (
                    <EmptyState
                        title="Nobody here yet"
                        hint="That is unusual — a company always has at least its owner. Try reloading."
                    />
                ) : (
                    <ul className="divide-y divide-separator">
                        {members.map(member => (
                            <MemberRow
                                key={member.memberId}
                                member={member}
                                offerable={offerable}
                                canManage={canManage}
                                disabledReason={whyNotEditable(context, members, member)}
                                busy={busy === member.memberId}
                                onRoleChange={roleId => run(
                                    member.memberId,
                                    () => changeMemberRole(member.memberId, roleId),
                                    `${member.displayName || member.email} is now in a different role.`,
                                )}
                                onStatusChange={status => run(
                                    member.memberId,
                                    () => setMemberStatus(member.memberId, status),
                                    status === 'suspended'
                                        ? 'They can no longer sign in to this company.'
                                        : 'They can sign in to this company again.',
                                )}
                                onRemove={() => setConfirming(member)}
                            />
                        ))}
                    </ul>
                )}
            </Card>

            {canManage && (
                <>
                    <div className="mt-9">
                        <SectionHeader
                            title="Invitations"
                            subtitle="Sent to one address, good once, and they expire."
                        />
                    </div>

                    {/* One surface for inviting and for what is outstanding: the form first, then the list, which says
                        so in a line when it is empty rather than standing as a tall empty card (2026-10 global sweep). */}
                    <Card className="mt-5 overflow-hidden">
                        <InviteForm
                            roles={offerable}
                            busy={busy === 'invite'}
                            onInvite={(email, roleId) => run(
                                'invite',
                                async () => { await inviteMember(context.workspace.id, email, roleId); },
                                `An invitation is on its way to ${email}.`,
                            )}
                        />
                        <div className="border-t border-separator">
                            {invites.length === 0 ? (
                                <p className="px-5 py-4 text-secondary text-label-3">No invitations outstanding.</p>
                            ) : (
                                <ul className="divide-y divide-separator">
                                    {invites.map(invite => (
                                        <InviteRow
                                            key={invite.id}
                                            invite={invite}
                                            busy={busy === invite.id}
                                            onResend={() => run(
                                                invite.id,
                                                async () => { await resendInvite(context.workspace.id, invite.id); },
                                                `Sent again to ${invite.email}. The earlier link no longer works.`,
                                            )}
                                            onRevoke={() => run(
                                                invite.id,
                                                () => revokeInvite(invite.id),
                                                `The invitation to ${invite.email} has been withdrawn.`,
                                            )}
                                        />
                                    ))}
                                </ul>
                            )}
                        </div>
                    </Card>
                </>
            )}

            <ConfirmDialog
                open={confirming !== null}
                title="Remove from this company?"
                confirmLabel="Remove"
                busy={busy === confirming?.memberId}
                onCancel={() => setConfirming(null)}
                onConfirm={() => {
                    const member = confirming;
                    setConfirming(null);
                    if (member) {
                        void run(
                            member.memberId,
                            () => removeMember(member.memberId),
                            `${member.displayName || member.email} is no longer in this company.`,
                        );
                    }
                }}
            >
                <p>
                    <strong>{confirming?.displayName || confirming?.email}</strong> will lose access to this
                    company immediately. Their follow-ups and history stay, and accounts assigned to them
                    become unassigned.
                </p>
                <p className="mt-2">They can be invited back later, as a new invitation.</p>
            </ConfirmDialog>
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

const STATUS_TONE = { active: 'pos', suspended: 'warn', removed: 'neutral' } as const;

function MemberRow({
    member, offerable, canManage, disabledReason, busy,
    onRoleChange, onStatusChange, onRemove,
}: {
    member: TeamMember;
    offerable: { id: string; name: string }[];
    canManage: boolean;
    disabledReason: string | null;
    busy: boolean;
    onRoleChange: (roleId: string) => void;
    onStatusChange: (status: 'active' | 'suspended') => void;
    onRemove: () => void;
}) {
    // The role the member is actually in may be one this person cannot assign —
    // an owner seen by a delegate, say. It is still shown, so the list is
    // truthful; it just cannot be chosen.
    const options = offerable.some(r => r.id === member.roleId)
        ? offerable
        : [{ id: member.roleId, name: member.roleName }, ...offerable];

    const locked = !canManage || disabledReason !== null || busy;

    return (
        <li className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
            {/* The name starts at 14rem and the controls wrap under it on a phone, rather than squeezing the name
                to its first letter and the note to a word a line (2026-10 global sweep). */}
            <div className="min-w-0 flex-1 basis-56">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-subhead font-semibold text-label truncate">
                        {member.displayName || member.email}
                    </span>
                    {member.isSelf && <Badge variant="text" tone="brand">You</Badge>}
                    {member.isOwner && <Badge variant="text" tone="neutral">Owner</Badge>}
                    {member.status !== 'active' && (
                        <Badge tone={STATUS_TONE[member.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
                            {member.status === 'suspended' ? 'Suspended' : member.status}
                        </Badge>
                    )}
                </div>
                <div className="text-secondary text-label-3 mt-0.5 truncate">{member.email}</div>
                {disabledReason && canManage && (
                    <div className="text-secondary text-label-3 mt-1.5">{disabledReason}</div>
                )}
            </div>

            {/* max-w-full, not flex-none: on a narrow phone the controls must
                wrap inside the row rather than run out of the card. */}
            <div className="flex items-center gap-2 flex-wrap max-w-full">
                {canManage ? (
                    <label className="sr-only" htmlFor={`role-${member.memberId}`}>
                        Role for {member.displayName || member.email}
                    </label>
                ) : null}
                {canManage ? (
                    <select
                        id={`role-${member.memberId}`}
                        value={member.roleId}
                        disabled={locked}
                        onChange={event => onRoleChange(event.target.value)}
                        className={fieldClass({ className: 'h-9' })}
                    >
                        {options.map(role => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                ) : (
                    <span className="text-secondary text-label-2">{member.roleName}</span>
                )}

                {canManage && (
                    <>
                        <Button
                            size="sm"
                            variant="quiet"
                            disabled={locked}
                            onClick={() => onStatusChange(member.status === 'active' ? 'suspended' : 'active')}
                        >
                            {member.status === 'active' ? 'Suspend' : 'Reinstate'}
                        </Button>
                        <Button size="sm" variant="quiet" disabled={locked} onClick={onRemove}>
                            Remove
                        </Button>
                    </>
                )}
            </div>
        </li>
    );
}

function InviteRow({
    invite, busy, onResend, onRevoke,
}: {
    invite: PendingInvite;
    busy: boolean;
    onResend: () => void;
    onRevoke: () => void;
}) {
    const expires = new Date(invite.expiresAt);
    return (
        <li className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1 basis-56">
                <div className="text-subhead font-semibold text-label truncate">{invite.email}</div>
                <div className="text-secondary text-label-3 mt-0.5">
                    {invite.roleName}
                    {invite.invitedByName && <> · invited by {invite.invitedByName}</>}
                    {' · '}
                    <time dateTime={invite.expiresAt}>
                        expires {expires.toISOString().slice(0, 10)}
                    </time>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-none">
                <Button size="sm" variant="row" disabled={busy} onClick={onResend}>
                    Send again
                </Button>
                <Button size="sm" variant="quiet" disabled={busy} onClick={onRevoke}>
                    Withdraw
                </Button>
            </div>
        </li>
    );
}

function InviteForm({
    roles, busy, onInvite,
}: {
    roles: AssignableRole[];
    busy: boolean;
    onInvite: (email: string, roleId: string) => void;
}) {
    // An invitation starts at the least a person could be given — not an
    // owner, the fewest permissions — so a hurried invite grants too little
    // rather than everything. The inviter raises it on purpose.
    const leastPowerful = [...roles]
        .sort((a, b) => Number(a.isOwner) - Number(b.isOwner) || a.capabilities.length - b.capabilities.length)[0]?.id ?? '';
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState(leastPowerful);
    const [problem, setProblem] = useState<string | null>(null);

    const chosen = roles.some(r => r.id === roleId) ? roleId : leastPowerful;

    return (
        // The fields keep their own space beneath them, so the block closes on that rather than adding more.
        <div className="px-5 pt-5 pb-1">
            <h3 className="text-subhead font-semibold text-label tracking-heading">Invite somebody</h3>
            <p className="text-secondary text-label-3 mt-1 mb-4 leading-relaxed">
                They will get an email with a link that works once and only from that address.
                Forwarding it does not pass the invitation on.
            </p>

            <form
                className="flex items-end gap-3 flex-wrap"
                // The browser's own validation bubble is unstyled, worded
                // differently in every browser, and silently prevents submit —
                // which means the sentence this product would have shown is
                // never reached. `type="email"` stays, for the keyboard it
                // brings up on a phone; the judgement is ours.
                noValidate
                onSubmit={event => {
                    event.preventDefault();
                    const address = normaliseEmail(email);
                    if (!isEmailShaped(address)) {
                        setProblem('That does not look like an email address.');
                        return;
                    }
                    if (!chosen) {
                        setProblem('Choose a role for them.');
                        return;
                    }
                    setProblem(null);
                    setEmail('');
                    onInvite(address, chosen);
                }}
            >
                <div className="flex-1 min-w-56">
                    <Field
                        label="Email address"
                        name="invite-email"
                        type="email"
                        autoComplete="off"
                        value={email}
                        error={problem}
                        onChange={event => setEmail(event.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="invite-role" className={labelClass}>
                        Role
                    </label>
                    <select
                        id="invite-role"
                        value={chosen}
                        onChange={event => setRoleId(event.target.value)}
                        className={fieldClass()}
                    >
                        {roles.map(role => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-4">
                    <Button type="submit" variant="primary" disabled={busy || roles.length === 0} busy={busy}>
                        {busy ? 'Sending…' : 'Send invitation'}
                    </Button>
                </div>
            </form>

            {roles.length === 0 && (
                <p className="text-secondary text-label-3">
                    There is no role you can invite somebody into. You can only offer a role whose
                    permissions you hold yourself.
                </p>
            )}
        </div>
    );
}
